import { SystemsService } from 'src/app/systems/state/systems.service';
import { UnverifiedSystem } from 'src/app/api/models/systems/systems.interface';
import { UnverifiedSystemsService } from 'src/app/radar/state/unverified-systems.service';
import { FeedbackStateEnum, FeedbackTypeEnum } from './../../api/models/feedback/feedback.enum';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { FeatureFlags } from 'src/app/api/models/profile/profile-feature-flags.enum';
import { TaskStatus } from '../models/tasks.enum';
import { DsrTaskOutcome } from '../models/dsr-task-outcome.enum';
import { FeedbackAnswer } from './../../api/models/feedback/feedback.interface';
import { tap, map, switchMap, first, BehaviorSubject, Observable } from 'rxjs';
import { ReviewerTask } from '../../api/models/feedback/feedback.interface';
import { ApiClientFeedbackService } from '../../api/api-client-feedback.service';
import { Injectable } from '@angular/core';
import { SystemInstance } from 'src/app/systems/models/systems.interface';

@Injectable({
  providedIn: 'root'
})
export class TasksService {

  private tasksSub = new BehaviorSubject<ReviewerTask[]>([]);
  tasksSub$ = this.tasksSub.asObservable();

  private openedTasksCount = new BehaviorSubject<number>(0);
  openedTasksCount$ = this.openedTasksCount.asObservable();

  private respondedTaskCount = new BehaviorSubject<number>(0);
  respondedTaskCount$ = this.respondedTaskCount.asObservable();

  constructor(private apiClientFeedback: ApiClientFeedbackService,
              private unverifiedSystemsService: UnverifiedSystemsService,
              private systemsService: SystemsService,
              private featureFlagQuery: FeatureFlagQuery) { }

  getTasks(): Observable<ReviewerTask[]> {
    return this.apiClientFeedback.getTasks().pipe(
      map(tasks => tasks.feedbacks),
      map(tasks => tasks.filter(task => this.isTaskVisible(task))),
      tap(tasks => this.updateCount(tasks)),
      tap(tasks => this.tasksSub.next(tasks))
    );
  }

  // Open = Pending. Completed = Responded for governance tasks (Canceled ones stay hidden, as before);
  // for DSR tasks every non-Pending state (Resolved / Canceled / Responded) is "completed".
  selectFilteredTasks(status: TaskStatus): Observable<ReviewerTask[]> {
    return this.tasksSub$.pipe(
      map(tasks => tasks.filter(task => status === TaskStatus.Open ? this.isOpen(task) : this.isCompleted(task))),
      );
  }

  private isOpen(task: ReviewerTask): boolean {
    return task.feedbackState === FeedbackStateEnum.Pending;
  }

  private isCompleted(task: ReviewerTask): boolean {
    return task.feedbackType === FeedbackTypeEnum.DsrTask
      ? task.feedbackState !== FeedbackStateEnum.Pending
      : task.feedbackState === FeedbackStateEnum.Responded;
  }

  // DSR tasks only exist behind DevDsrCollaboratorTasks — with the flag off the page must look as before
  private isTaskVisible(task: ReviewerTask): boolean {
    return task.feedbackType !== FeedbackTypeEnum.DsrTask || this.featureFlagQuery.getFlag(FeatureFlags.DevDsrCollaboratorTasks);
  }

  sendResponse(answer: FeedbackAnswer, id: string): Observable<void> {
    return this.apiClientFeedback.reviewerRespondFeedback(answer, id).pipe(
      switchMap(res => this.updateTasks(res.feedbackData[0])),
    );
  }

  // DSR task (DevDsrCollaboratorTasks) — BE contract: completed / attached → Resolved, no data found (copy) → Responded,
  // couldn't complete / attach → Canceled (note required, becomes the integration's error message)
  sendDsrResponse(task: ReviewerTask, outcome: DsrTaskOutcome, note: string): Observable<void> {
    const answer: FeedbackAnswer = { newState: this.toFeedbackState(outcome), answerText: note };
    return this.apiClientFeedback.reviewerRespondFeedback(answer, task.feedbackId).pipe(
      switchMap(res => this.updateTasks(res.feedbackData[0])),
    );
  }

  private toFeedbackState(outcome: DsrTaskOutcome): FeedbackStateEnum {
    switch (outcome) {
      case DsrTaskOutcome.NotCompleted:
      case DsrTaskOutcome.NotAttached:
        return FeedbackStateEnum.Canceled;
      case DsrTaskOutcome.NoData:
        return FeedbackStateEnum.Responded;
      default:
        return FeedbackStateEnum.Resolved;
    }
  }

  private updateTasks(updatedTask: ReviewerTask): Observable<void> {
    return this.tasksSub$.pipe(
      first(),
      map(tasks => tasks.filter(task => task.feedbackId !== updatedTask.feedbackId)),
      map(tasks => [...tasks, updatedTask]),
      tap(tasks => this.tasksSub.next(tasks)),
      tap(tasks => this.updateCount(tasks)),
      map(() => void 0)
    );
  }

  updateCount(tasks: ReviewerTask[]): void {
    this.openedTasksCount.next(tasks.filter(task => this.isOpen(task)).length);
    this.respondedTaskCount.next(tasks.filter(task => this.isCompleted(task)).length);
  }

  getUnverifiedSystemData(systemId: string): Observable<UnverifiedSystem> {
    return this.unverifiedSystemsService.getUnverifiedSystem(systemId);
  }

  getInventorySystemData(systemId: string): Observable<SystemInstance> {
    return this.systemsService.getSystemInstanceById(systemId);
  }
}
